import { useCallback } from 'react';

import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import ClobePDFModal from '@revenuemarket/ui/ModalPdf/ClobePDFModal';
import { sanitizeCallbackUrl } from '@revenuemarket/utils';
import { Modal, Typography } from 'antd';
import { useRouter } from 'next/router';

import getNeededAgreements from '~apis/companyuser/getNeedAgreements';
import updateAgreements from '~apis/companyuser/updateAgreements';
import { AuthModel } from '~models/auth';
import { PolicyModel } from '~models/policy';
import { deserializeAccessToken } from '~utils/auth';
import getTargetCompany from '~utils/auth/getTargetCompany';
import { getCompanyInvitationSession } from '~utils/sesion/companyInvitationSession';

import Agreement = PolicyModel.Agreement;

export function useHandleLoginSuccess() {
  const [modal, contextHolder] = Modal.useModal();
  const router = useRouter();

  // 반환: 실제 콜백 함수와 contextHolder(필요시)
  const handleLoginSuccess = useCallback(
    async ({
      accessToken,
      callbackUrl,
      values,
    }: {
      accessToken: string;
      callbackUrl?: string;
      values?: AuthModel.SignInRequest;
    }) => {
      // callbackUrl 검증 및 정제 (상대경로만 허용)
      const sanitizedCallbackUrl = sanitizeCallbackUrl(callbackUrl, '/clobe/home');

      if (sanitizedCallbackUrl.split('?')[0] === '/auth/join-by-invitation') {
        router.push(sanitizedCallbackUrl);
        return;
      }

      // MEMO:로그인 이후 유저 이메일을 알수 있는구좌가 없음
      if (values?.email) {
        await sessionStorage.setItem('user-email', values.email);
      }

      const companyInvitationSession = getCompanyInvitationSession();
      if (companyInvitationSession !== undefined) {
        await router.push(
          `/accept/company/invitation/auto?token=${companyInvitationSession.token}`,
        );
        return;
      }

      const session = deserializeAccessToken(accessToken);
      const companyId = await getTargetCompany(session.userId);

      const skipCompanyIdCheck = router.query.checkCompanyId === 'false';

      if (!companyId) {
        if (skipCompanyIdCheck) {
          router.push(sanitizedCallbackUrl);
          return;
        }
        router.push('/auth/create-workspace');
        return;
      }

      const redirectUrl = sanitizedCallbackUrl;

      const userId = session.userId;
      const isDemoTrial = session.iss === 'DEMO_TRIAL';
      const neededAgreementsResponse = await getNeededAgreements(companyId, userId);
      const companyAgreements = neededAgreementsResponse?.Data?.data?.companyAgreements || [];
      const userAgreements = neededAgreementsResponse?.Data?.data?.userAgreements || [];
      const concatenatedAgreements = companyAgreements.concat(userAgreements);
      if (concatenatedAgreements.length > 0 && !isDemoTrial) {
        modal.confirm({
          icon: null,
          title: '새로운 약관에 동의해주세요',
          content: (
            <Flex direction="column" gap={16}>
              <Typography.Text color={theme.colorTokens.textDefault}>
                편리하고 안전한 서비스를 위해 클로브가 새로운 약관을 준비했습니다.
              </Typography.Text>
              <ul className="gap-[4px]">
                {concatenatedAgreements.map((agreement: Agreement) => (
                  <li
                    key={agreement.id}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      overlays.open(({ isOpen, onClose }) => (
                        <ClobePDFModal isOpen={isOpen} onClose={onClose} url={agreement.url} />
                      ));
                    }}
                  >
                    <Typography.Text style={{ color: theme.colorTokens.textPrimary }}>
                      [{agreement.required ? '필수' : '선택'}] {agreement.title}
                    </Typography.Text>
                  </li>
                ))}
              </ul>
            </Flex>
          ),
          cancelText: '필수 항목 동의',
          onCancel: () => {
            updateAgreements(
              companyId,
              userId,
              companyAgreements.filter((a) => a.required).map((a) => a.id),
              userAgreements.filter((a) => a.required).map((a) => a.id),
            );
            router.push(redirectUrl);
          },
          okText: '전체 동의하기',
          onOk: () => {
            updateAgreements(
              companyId,
              userId,
              companyAgreements.map((a) => a.id),
              userAgreements.map((a) => a.id),
            );
            router.push(redirectUrl);
          },
        });
        return;
      }

      window.location.href = redirectUrl;
    },
    [router, modal],
  );

  return { handleLoginSuccess, contextHolder };
}
